import React, { useState } from 'react'
import { CategoryBaseWithThumbnailFragment } from '@graphql/fragments/__generated__/CategoryWithThumbnail.generated'
import { Collapse, Divider, IconButton } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { CartLinesQueryResult, useCartLinesQuery } from '@graphql/queries/__generated__/cart.generated'
import Pagination from '@mui/material/Pagination'
import { useTranslation } from 'react-i18next'
import PaginateByButton from '@components/PaginateByButton'
import FormattedPrice from '@components/FormattedPrice'
import _ from 'lodash'
import { CartTypeEnum } from '../../../generated/gqlTypes'
import CartLines from '@components/WholesaleCartLines/CartLines'
import DeleteIcon from '@icons/DeleteIcon'

interface Props {
  category: CategoryBaseWithThumbnailFragment
  lineLoading: number | null
  initialize: boolean
  categoryLinesInfo: { pk: number; qty: number; pcs: number; price: number; currency: string }[]
  readonly: boolean

  handleEditQty(linePk: number, qty: number, variantPk: number): Promise<void>

  handleRemoveLine(linePk: number, variantPk: number): Promise<void>

  handleEditLine(linePk: number): Promise<void>

  onClickProduct(specId: string | number, slug: string): void

  handleApplySupplyToAll(linePk: string): Promise<void>

  cartClear(categoryId?: string): Promise<void>
}

const CartLinesCategory: React.FC<Props> = ({
  category,
  handleEditQty,
  handleEditLine,
  handleRemoveLine,
  lineLoading,
  readonly,
  onClickProduct,
  handleApplySupplyToAll,
  initialize,
  cartClear,
  categoryLinesInfo,
}) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<boolean>(initialize)
  const [page, setPage] = useState(1)
  const [paginateBy, setPaginateBy] = useState(10)
  const [cartLinesData, setCartLinesData] = useState<CartLinesQueryResult['data']>(null)
  const cartLinesQuery = useCartLinesQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !expanded,
    variables: {
      limit: paginateBy,
      offset: (page - 1) * paginateBy,
      cartType: CartTypeEnum.Wholesale,
      categories: [category.pk],
      supplyIsNull: true,
      // The wholesale cart lines list only needs each kit's wholesale price - the rest
      // (product specifics, etc.) is fetched on demand by CartLineKitContents on expand.
      includeSupplyPartDetails: false,
    },
    onCompleted: setCartLinesData,
  })

  const onToggleExpanded = () => {
    setExpanded(prev => !prev)
  }

  const onChangePage = (e, page: number) => {
    setPage(page)
  }
  const onRemoveLine = async (linePk: number, variantPk: number) => {
    await handleRemoveLine(linePk, variantPk)
  }
  const lines = cartLinesData?.cartLines.results || []
  const itemsCount = categoryLinesInfo.length
  const numPages = Math.ceil((cartLinesData?.cartLines.totalCount || 0) / paginateBy)
  const isLoading = cartLinesQuery.loading
  const pcs = _.sumBy(categoryLinesInfo, line => line.pcs * line.qty || line.qty)
  const price = _.sumBy(categoryLinesInfo, line => line.price * line.qty)
  const qty = _.sumBy(categoryLinesInfo, line => line.qty)
  const currency = categoryLinesInfo[0]?.currency || 'USD'
  return (
    <>
      <div className='bg-white shadow-card mb-20 relative rounded-lg overflow-hidden'>
        <div
          className='flex flex-row items-center justify-between py-10 px-20 cursor-pointer gap-8'
          onClick={onToggleExpanded}
          role='button'
          title={expanded ? t('button.hide', 'Hide') : t('button.show', 'Show')}
        >
          <div className='flex flex-col sm:flex-row sm:items-center min-w-0 sm:my-[5px]'>
            <div className='flex flex-row items-center flex-wrap'>
              <IconButton>
                {expanded ? <ExpandLessIcon fontSize='large' /> : <ExpandMoreIcon fontSize='large' />}
              </IconButton>
              <h1 className='font-bold text-lg uppercase mx-10'>{category.name}</h1>
              {itemsCount > 0 && (
                <span className='hidden sm:inline text-13 text-gray-700 leading-18'>
                  {pcs
                    ? t('designsWithCount', { defaultValue: '{{count}} Designs', count: itemsCount })
                    : t('itemsWithCount', { defaultValue: '{{count}} Items', count: itemsCount })}
                  &nbsp;-&nbsp;
                  {t('pcsWithCount', { defaultValue: '{{count}}pcs', count: qty })}
                  &nbsp;-&nbsp;
                  <FormattedPrice amount={price} currency={currency} />
                </span>
              )}
            </div>
            {itemsCount > 0 && (
              <span className='sm:hidden pl-[40px] text-13 text-gray-700 leading-18'>
                {pcs
                  ? t('designsWithCount', { defaultValue: '{{count}} Designs', count: itemsCount })
                  : t('itemsWithCount', { defaultValue: '{{count}} Items', count: itemsCount })}
                &nbsp;-&nbsp;
                {t('pcsWithCount', { defaultValue: '{{count}}pcs', count: qty })}
                &nbsp;-&nbsp;
                <FormattedPrice amount={price} currency={currency} />
              </span>
            )}
          </div>
          <div className='flex items-center flex-shrink-0'>
            <div className='hidden sm:block'>
              <PaginateByButton compact paginateBy={paginateBy} setPaginateBy={setPaginateBy} />
            </div>
            {!readonly && (
              <IconButton
                className='flex-shrink-0'
                onClick={e => {
                  e.stopPropagation()
                  cartClear(category.id)
                }}
                sx={{ marginLeft: '16px' }}
                title={t('button.remove', 'Remove')}
              >
                <DeleteIcon viewBox='0 -960 960 960' color='warning' />
              </IconButton>
            )}
          </div>
        </div>
        <Divider />
        <Collapse in={expanded} mountOnEnter unmountOnExit>
          <CartLines
            lines={lines}
            isLoading={isLoading}
            handleEditQty={handleEditQty}
            handleEditLine={handleEditLine}
            handleRemoveLine={onRemoveLine}
            lineLoading={lineLoading}
            onClickProduct={onClickProduct}
            handleApplySupplyToAll={handleApplySupplyToAll}
            readonly={readonly}
          />
        </Collapse>
        {expanded && numPages > 1 && (
          <div className='flex items-center justify-center pb-20'>
            <Pagination
              count={numPages}
              color='secondary'
              variant='text'
              page={page}
              size='small'
              onChange={onChangePage}
              siblingCount={1}
              shape='rounded'
            />
          </div>
        )}
      </div>
    </>
  )
}

export default CartLinesCategory
